import { array, boolean, number, object, string } from "yup";
import { useForm, useFormState } from "react-hook-form";
import { CUSTOM_FIELD_TYPE } from "../misc/constants";
import Validations from "@helpers/validations";
import size from "lodash/size";
import useAxios from "@hooks/use-axios";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";

const defaultValues = {
    label        : "",
    type         : "",
    required     : false,
    placeholder  : "",
    default_value: "",
    options      : [{ label: "", value: "" }],
    validation   : {
        minLength: null,
        maxLength: null,
        minVal   : null,
        maxVal   : null
    },
    order : null,
    status: "active"
};

const useUpsert = (props) => {
    const {
        modalConfig: { row },
        handleModal,
        getCustomAttributes
    } = props;

    const { loading, axiosInstance } = useAxios({ base: "panel" });
    const { requiredValidation, minLessThanMaxValidation } = Validations();
    const { platformId } = useParams();

    const methods = useForm({
        defaultValues,
        resolver: yupResolver(
            object().shape({
                label        : requiredValidation({ name: "Label" }),
                type         : requiredValidation({ name: "Type" }),
                required     : boolean(),
                placeholder  : string(),
                default_value: string(),

                options: array().when("type", {
                    is  : (type) => type === "select",
                    then: (schema) => schema
                        .of(
                            object().shape({
                                label: string().required("Option label is required"),
                                value: string().required("Option value is required")
                            })
                        )
                        .min(1, "At least one option is required"),
                    otherwise: (schema) => schema.notRequired().nullable()
                }),

                validation: object().when(["type"], ([type], schema) => {
                    if (
                        [CUSTOM_FIELD_TYPE.TEXT, CUSTOM_FIELD_TYPE.TEXT_AREA].includes(type)
                    ) {
                        return schema.shape({
                            minLength: minLessThanMaxValidation({
                                maxField: "maxLength",
                                label   : "Min Length"
                            }).required("Min Length is required"),
                            maxLength: number()
                                .typeError("Max Length must be a number")
                                .required("Max Length is required"),
                            minVal: number().nullable(),
                            maxVal: number().nullable()
                        });
                    }

                    if (type === "number") {
                        return schema.shape({
                            minVal: minLessThanMaxValidation({
                                maxField: "maxVal",
                                label   : "Min Value"
                            }).required("Min Value is required"),
                            maxVal: number()
                                .typeError("Max Value must be a number")
                                .required("Max Value is required"),
                            minLength: number().nullable(),
                            maxLength: number().nullable()
                        });
                    }

                    return schema.shape({
                        minLength: number().nullable(),
                        maxLength: number().nullable(),
                        minVal   : number().nullable(),
                        maxVal   : number().nullable()
                    });
                }),

                order : number().nullable().positive("Must be positive"),
                status: string()
            })
        )
    });

    const { control, handleSubmit, reset } = methods;
    const { isDirty } = useFormState({ control });

    useEffect(() => {
        if (size(row)) {
            reset({
                label        : row.label || "",
                type         : row.type || "",
                required     : row.required || false,
                placeholder  : row.placeholder || "",
                default_value: row.default_value || "",
                options      : size(row.options) ? row.options : [{ label: "", value: "" }],
                validation   : {
                    minLength: row.validation?.minLength ?? null,
                    maxLength: row.validation?.maxLength ?? null,
                    minVal   : row.validation?.minVal ?? null,
                    maxVal   : row.validation?.maxVal ?? null
                },
                order : row.order || null,
                status: row.status || "active"
            });
        } else {
            reset(defaultValues);
        }
    }, [row]);

    const onSubmit = handleSubmit(async (data) => {
        const validation = {};

        if (
            [CUSTOM_FIELD_TYPE.TEXT_AREA, CUSTOM_FIELD_TYPE.TEXT].includes(data.type)
        ) {
            if (data.validation.minLength !== null) validation.minLength = data.validation.minLength;
            if (data.validation.maxLength !== null) validation.maxLength = data.validation.maxLength;
        }

        if (data.type === "number") {
            if (data.validation.minVal !== null) validation.minVal = data.validation.minVal;
            if (data.validation.maxVal !== null) validation.maxVal = data.validation.maxVal;
        }

        const payload = {
            label        : data.label,
            type         : data.type,
            form_type    : "client",
            required     : data.required,
            placeholder  : data.placeholder || "",
            default_value: data.default_value || "",
            order        : data.order || "",
            status       : data.status
        };

        if (data.type === "select") {
            payload.options = data.options;
        }

        if (Object.keys(validation).length > 0) {
            payload.validation = validation;
        }

        Object.keys(payload).forEach(
            (key) => payload[key] === undefined && delete payload[key]
        );

        if (row._id) {
            await axiosInstance.patch(`/field/${platformId}/${row._id}`, payload);
        } else {
            await axiosInstance.post(`/field/${platformId}`, payload);
        }

        handleModal();
        getCustomAttributes();
    });

    return {
        onSubmit,
        methods,
        updateLoading: loading,
        isDirty
    };
};

export default useUpsert;
